import { useEffect, useState } from "react";
import { Button, cx, Dialog, ErrorText, Field, Spinner } from "./ui.tsx";

/** Hard navigation on purpose: the session cookie changed, so every cached query is someone else's now. */
async function post(path: string, body?: unknown) {
  // Always a JSON body: the auth endpoints refuse a JSON content type with nothing in it.
  const res = await fetch(path, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body ?? {}) });
  const data = (await res.json().catch(() => ({}))) as { message?: string; error?: string; restored?: boolean };
  if (!res.ok) throw new Error(data.message ?? data.error ?? `Request failed (${res.status})`);
  return data;
}

type Mode = "view" | "act";

/** Admin → Users → "View as": pick a mode, say why (it goes in the audit log), and switch to their account. */
export function ImpersonateDialog({ user, onClose }: { user: { id: string; name: string; email: string } | null; onClose: () => void }) {
  const [mode, setMode] = useState<Mode>("view");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    setMode("view");
    setReason("");
    setError(null);
    setBusy(false);
  }, [user?.id]);
  const first = user?.name.split(" ")[0] ?? "them";

  const option = (m: Mode, title: string, text: string) => (
    <label className={cx("flex cursor-pointer gap-3 rounded-lg border p-3 text-sm", mode === m ? "border-[var(--color-accent)] bg-[var(--panel-2)]" : "border-[var(--line)]")}>
      <input type="radio" name="mode" checked={mode === m} onChange={() => setMode(m)} className="mt-1" />
      <span>
        <span className="block font-medium">{title}</span>
        <span className="block text-xs text-[var(--muted)]">{text}</span>
      </span>
    </label>
  );

  return (
    <Dialog open={!!user} onClose={onClose} title={`View as ${user?.name ?? ""}`}>
      <form
        className="space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            await post("/api/auth/krut/impersonate", { userId: user!.id, mode, reason });
            location.href = "/";
          } catch (err) {
            setError(err);
            setBusy(false);
          }
        }}
      >
        <p className="text-sm text-[var(--muted)]">
          You'll see {user?.email}'s account exactly as {first} does: their chats, files, tasks, skills and memory. It lasts up to an hour, and it's recorded in the audit log under your name.
        </p>
        <div className="space-y-2">
          {option("view", "View only (recommended)", `Look around. Nothing can be sent, saved or changed.`)}
          {option("act", `Act as ${first}`, `You can chat with their assistant and change things as them. Anything you send is saved in their history and memory, as if ${first} wrote it.`)}
        </div>
        <p className="text-xs text-[var(--muted)]">In both modes you can never read their secret values, decide their approvals, or change their connected accounts, MCP servers or chat-account links. In act mode you can add or replace their secrets.</p>
        <Field label="Why are you doing this?" hint="Recorded in the audit log. For example: a support ticket, or checking a permissions problem.">
          <textarea
            required
            minLength={5}
            maxLength={300}
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 py-2 text-sm outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20"
          />
        </Field>
        <ErrorText error={error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={busy || reason.trim().length < 5}>{busy ? <Spinner /> : mode === "view" ? `View as ${first}` : `Act as ${first}`}</Button>
        </div>
      </form>
    </Dialog>
  );
}

export const BANNER_HEIGHT = "2.5rem";

/** Across the top of every page while an admin is acting as someone: who, which mode, time left, and the way out. */
export function ImpersonationBanner({ asUser, mode, adminEmail, expiresAt }: { asUser: { name: string; email: string }; mode: Mode; adminEmail: string; expiresAt: string }) {
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(t);
  }, []);
  const minutes = Math.max(0, Math.ceil((new Date(expiresAt).getTime() - now) / 60_000));
  const stop = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await post("/api/auth/krut/stop-impersonating");
      location.href = r.restored ? "/admin/people/users" : "/login";
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };
  return (
    <div
      role="status"
      style={{ height: BANNER_HEIGHT }}
      className={cx("sticky top-0 z-50 flex items-center justify-center gap-3 px-3 text-sm font-medium text-white shadow", mode === "act" ? "bg-red-700" : "bg-amber-600")}
    >
      <span className="truncate">
        {mode === "act" ? "Acting as" : "Viewing as"} <b>{asUser.name}</b> ({asUser.email}) · {mode === "act" ? "changes are saved as them" : "view only"} · {minutes} min left · you are {adminEmail}
      </span>
      {error && <span className="hidden text-xs sm:inline">{error}</span>}
      <button onClick={stop} disabled={busy} className="shrink-0 rounded-md bg-white/20 px-3 py-1 text-xs font-semibold hover:bg-white/30 disabled:opacity-60">
        {busy ? "Stopping…" : "Stop"}
      </button>
    </div>
  );
}
